import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  signal,
  computed,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  RealtimeService,
  RealtimeEvent,
} from "../../../core/services/realtime.service";
import { StateManagementService } from "../../../core/services/state-management.service";
import { NotificationsRealtimeService } from "../../../core/services/notifications-realtime.service";
import {
  NotificationsApiService,
  Notification as ApiNotification,
} from "../../../features/notifications/services/notifications-api.service";
import { Subject, takeUntil } from "rxjs";

export interface ToastNotification {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
  read: boolean;
  actionUrl?: string;
  createdAt: string;
  readAt?: string;
  followUpDueAt?: string;
  followUpInterval?: string;
  followUpCount?: number;
  maxFollowUps?: number;
  actionRequired?: boolean;
  actionTakenAt?: string;
  relatedTaskId?: string;
  relatedResourceType?: string;
  relatedResourceId?: string;
  // Additional properties for toast notifications
  duration?: number;
  persistent?: boolean;
  action?: {
    label: string;
    handler: () => void;
  };
}

@Component({
  selector: "app-advanced-notifications",
  standalone: true,
  imports: [CommonModule],
  template: `
    <!-- Debug: Show notification count (always visible for debugging) -->

    <!-- Notifications Container -->
    <div
      class="fixed top-12 right-4 z-[9999] flex flex-col gap-2 max-w-md w-full pointer-events-none"
    >
      <div
        *ngFor="let notification of notifications(); trackBy: trackById"
        [class]="getNotificationClasses(notification)"
        class="pointer-events-auto shadow-2xl rounded-lg p-4 transform transition-all duration-300 animate-in slide-in-from-right min-w-[320px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
        (click)="markAsRead(notification.id)"
      >
        <div class="flex items-start gap-3">
          <div
            [class]="getIconClasses(notification.type)"
            class="flex-shrink-0 mt-0.5"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <ng-container [ngSwitch]="notification.type">
                <path
                  *ngSwitchCase="'success'"
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
                <path
                  *ngSwitchCase="'error'"
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
                <path
                  *ngSwitchCase="'warning'"
                  fill-rule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clip-rule="evenodd"
                />
                <path
                  *ngSwitchDefault
                  fill-rule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clip-rule="evenodd"
                />
              </ng-container>
            </svg>
          </div>
          <div class="flex-1 min-w-0">
            <h4 class="text-sm font-semibold text-gray-900 dark:text-white">
              {{ notification.title }}
            </h4>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
              {{ notification.message }}
            </p>
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {{ notification.createdAt | date: "short" }}
            </p>
            <button
              *ngIf="notification.action"
              (click)="
                notification.action!.handler();
                markAsRead(notification.id);
                $event.stopPropagation()
              "
              class="mt-2 text-xs font-medium underline"
            >
              {{ notification.action.label }}
            </button>
          </div>
          <button
            (click)="remove(notification.id); $event.stopPropagation()"
            class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      @keyframes slide-in-from-right {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }

      .animate-in {
        animation: slide-in-from-right 0.3s ease-out;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Default, // Use default to ensure notifications are detected
})
export class AdvancedNotificationsComponent implements OnInit, OnDestroy {
  private readonly realtimeService = inject(RealtimeService);
  private readonly notificationsRealtime = inject(NotificationsRealtimeService);
  private readonly notificationsApi = inject(NotificationsApiService);
  private readonly stateService = inject(StateManagementService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly _notifications = signal<ToastNotification[]>([]);

  // Track last notification timestamp to detect new notifications from REST API
  private lastNotificationTimestamp: string | null = null;

  readonly notifications = computed(() => this._notifications());

  ngOnInit(): void {
    console.log("🚀 AdvancedNotificationsComponent initialized");

    // Make test method available globally for debugging
    (window as any).testNotification = () => this.testNotification();

    // Request browser notification permission on startup
    this.requestNotificationPermission();

    // Ensure notifications WebSocket is connected for the current user
    this.notificationsRealtime.connect();

    // Log connection status
    console.log(
      "📡 WebSocket connection status:",
      this.notificationsRealtime.connected(),
    );

    // WebSocket will handle all real-time notifications automatically
    // But we also need to monitor REST API for notifications that don't come through WebSocket
    console.log("🔍 Loading initial notification from REST API...");
    this.notificationsApi.getNotifications({ limit: 1, offset: 0 }).subscribe({
      next: (res) => {
        console.log("📋 Initial notification load response:", res);
        if (res.success && res.data && res.data.length > 0) {
          const latestNotification = res.data[0];
          console.log("Latest notification on page load:", latestNotification);
          this.show({
            type: this.mapBackendType(latestNotification.type),
            title: latestNotification.title || "New Notification",
            message: latestNotification.message || "",
            action: latestNotification.actionUrl
              ? {
                  label: "View",
                  handler: () => {
                    // Navigation is handled at the router level; for now we just rely on URL
                    window.location.href = latestNotification.actionUrl!;
                  },
                }
              : undefined,
          });
          this.maybeShowBrowserNotification(
            latestNotification.title,
            latestNotification.message,
            latestNotification.actionUrl,
          );
          
          // Set the last notification timestamp to avoid showing this notification again
          this.lastNotificationTimestamp = latestNotification.createdAt;
          console.log("⏰ Set initial last notification timestamp:", this.lastNotificationTimestamp);
        } else {
          console.log("✅ No initial notifications found");
        }
      },
      error: (err) => {
        console.warn("Failed to load latest notification on page load:", err);
      },
    });

    // Check for new notifications from REST API every 30 seconds as fallback
    this.checkForNewNotifications();
    const interval = setInterval(() => {
      this.checkForNewNotifications();
    }, 30000); // Check every 30 seconds

    // Clean up interval on destroy
    this.destroy$.subscribe(() => {
      clearInterval(interval);
    });

    // Show a test notification after 2 seconds to verify component is working
    // setTimeout(() => {
    //   console.log('🧪 Showing test notification...');
    //   this.testNotification();
    // }, 2000);

    // Listen to dedicated notifications WebSocket stream - SHOW TOASTS BY DEFAULT
    this.notificationsRealtime.notifications$
      .pipe(takeUntil(this.destroy$))
      .subscribe((notification) => {
        console.log(
          "📨 Received notification in AdvancedNotificationsComponent:",
          notification,
        );
        console.log("🔔 Showing WebSocket notification:", notification.title);
        this.show({
          type: this.mapBackendType(notification.type),
          title: notification.title || "New Notification",
          message: notification.message || "",
          action: notification.actionUrl
            ? {
                label: "View",
                handler: () => {
                  // Navigation is handled at the router level; for now we just rely on URL
                  window.location.href = notification.actionUrl!;
                },
              }
            : undefined,
        });
        this.maybeShowBrowserNotification(
          notification.title,
          notification.message,
          notification.actionUrl,
        );
      });

    // Listen to realtime events
    this.realtimeService.events
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => {
        this.handleRealtimeEvent(event);
      });

    // Listen to state service success/error messages
    this.stateService.success$
      .pipe(takeUntil(this.destroy$))
      .subscribe((message) => {
        if (message) {
          this.showSuccess(message);
        }
      });

    this.stateService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        if (error) {
          this.showError(error);
        }
      });

    // WebSocket provides real-time notifications, no need for REST API polling
    // The notificationsRealtime.notifications$ subscription handles real-time updates
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  show(
    notification: Omit<ToastNotification, "id" | "createdAt" | "read">,
  ): string {
    console.log("🎯 Showing notification toast:", notification);
    const id = "notification-" + Date.now() + "-" + Math.random().toString(36).substr(2, 9);
    const newNotification: ToastNotification = {
      ...notification,
      id,
      createdAt: new Date().toISOString(),
      read: false,
    };

    const current = this._notifications();
    this._notifications.set([...current, newNotification]);
    console.log("📋 Total notifications now:", this._notifications().length);
    console.log("📋 Notifications array:", this._notifications());
    // Force change detection
    this.cdr.markForCheck();

    // Auto-remove after duration unless persistent
    if (!notification.persistent && notification.duration !== 0) {
      const duration = notification.duration || 5000;
      setTimeout(() => {
        this.remove(id);
      }, duration);
    }

    return id;
  }

  showSuccess(message: string, title: string = "Success"): string {
    return this.show({
      type: "success",
      title,
      message,
      duration: 3000,
    });
  }

  showError(message: string, title: string = "Error"): string {
    return this.show({
      type: "error",
      title,
      message,
      duration: 5000,
      persistent: true,
    });
  }

  showWarning(message: string, title: string = "Warning"): string {
    return this.show({
      type: "warning",
      title,
      message,
      duration: 4000,
    });
  }

  showInfo(message: string, title: string = "Information"): string {
    return this.show({
      type: "info",
      title,
      message,
      duration: 3000,
    });
  }

  remove(id: string): void {
    const current = this._notifications();
    this._notifications.set(
      current.filter((n: ToastNotification) => n.id !== id),
    );
  }

  markAsRead(id: string): void {
    const current = this._notifications();
    this._notifications.set(
      current.map((n: ToastNotification) =>
        n.id === id ? { ...n, read: true } : n,
      ),
    );
  }

  clear(): void {
    this._notifications.set([]);
  }

  private handleRealtimeEvent(event: RealtimeEvent): void {
    if (event.type === "notification:new") {
      this.show({
        type: event.payload.type || "info",
        title: event.payload.title || "New Notification",
        message: event.payload.message || JSON.stringify(event.payload),
        action: event.payload.action,
      });
    } else if (event.type === "task:created") {
      this.showInfo(
        "Task \"" + (event.payload.title || "New task") + "\" created",
        "Task Created",
      );
    } else if (event.type === "task:updated") {
      this.showInfo(
        "Task \"" + (event.payload.title || "Task") + "\" updated",
        "Task Updated",
      );
    }
  }

  private mapBackendType(type: string | undefined): ToastNotification["type"] {
    const normalized = (type || "").toLowerCase();
    switch (normalized) {
      case "success":
      case "info":
      case "warning":
      case "error":
        return normalized as ToastNotification["type"];
      case "security":
      case "due_date_reminder":
        return "warning";
      default:
        return "info";
    }
  }

  private requestNotificationPermission(): void {
    if (typeof Notification === "undefined") {
      console.warn("Browser notifications are not supported in this browser");
      return;
    }

    if (Notification.permission === "default") {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") {
          console.log("✅ Browser notification permission granted");
          // Show a welcome notification to confirm it works
          new Notification("Notifications Enabled", {
            body: "You will now receive push notifications from MIS",
            icon: "/favicon.ico",
            tag: "welcome",
          });
        } else if (permission === "denied") {
          console.warn("❌ Browser notification permission denied");
        }
      });
    } else if (Notification.permission === "granted") {
      console.log("✅ Browser notifications already enabled");
    }
  }

  private maybeShowBrowserNotification(
    title?: string,
    message?: string,
    actionUrl?: string,
  ): void {
    if (typeof Notification === "undefined") {
      return;
    }

    if (Notification.permission !== "granted") {
      // Permission not granted, skip browser notification
      return;
    }

    try {
      const notification = new Notification(title || "New Notification", {
        body: message || "",
        icon: "/favicon.ico",
        badge: "/favicon.ico",
        tag: "notification-" + Date.now(),
        requireInteraction: false,
        silent: false,
      });

      // Handle notification click - navigate to action URL if provided
      notification.onclick = () => {
        window.focus();
        if (actionUrl) {
          window.location.href = actionUrl;
        }
        notification.close();
      };

      // Auto-close after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (error) {
      console.error("Failed to show browser notification:", error);
    }
  }

  getNotificationClasses(notification: ToastNotification): string {
    const base = "bg-white dark:bg-gray-800 border-l-4";
    const typeMap = {
      success: base + " border-green-500",
      error: base + " border-red-500",
      warning: base + " border-yellow-500",
      info: base + " border-blue-500",
    };
    return typeMap[notification.type as keyof typeof typeMap] || typeMap.info;
  }

  getIconClasses(type: string): string {
    const typeMap = {
      success: "text-green-500",
      error: "text-red-500",
      warning: "text-yellow-500",
      info: "text-blue-500",
    };
    return typeMap[type as keyof typeof typeMap] || typeMap.info;
  }

  /** 
   * Check for new notifications from REST API and show them as toast notifications
   */
  private checkForNewNotifications(): void {
    console.log("🔍 Checking for new notifications from REST API...");
    console.log("Last notification timestamp:", this.lastNotificationTimestamp);
    
    this.notificationsApi.getNotifications({ limit: 10, offset: 0 }).subscribe({
      next: (res) => {
        console.log("📋 Received response from notifications API:", res);
        if (res.success && res.data) {
          console.log("📋 Received notifications from API:", res.data.length);
          const newNotifications = this.getNewNotificationsFromApi(res.data);
          console.log("🆕 New notifications found:", newNotifications.length);
          
          if (newNotifications.length > 0) {
            console.log(
              "📨 Found " + newNotifications.length + " new notification(s) from REST API",
            );

            newNotifications.forEach((notification: ApiNotification) => {
              console.log("🔔 Showing notification:", notification.title);
              this.show({
                type: this.mapBackendType(notification.type),
                title: notification.title || "New Notification",
                message: notification.message || "",
                action: notification.actionUrl
                  ? {
                      label: "View",
                      handler: () => {
                        // Navigation is handled at the router level; for now we just rely on URL
                        window.location.href = notification.actionUrl!;
                      },
                    }
                  : undefined,
              });
              this.maybeShowBrowserNotification(
                notification.title,
                notification.message,
                notification.actionUrl,
              );
            });

            // Update last notification timestamp
            if (res.data.length > 0) {
              const latestNotification = res.data.reduce((latest, current) =>
                new Date(current.createdAt) > new Date(latest.createdAt)
                  ? current
                  : latest,
              );
              this.lastNotificationTimestamp = latestNotification.createdAt;
              console.log("⏰ Updated last notification timestamp:", this.lastNotificationTimestamp);
            }
          } else {
            console.log("✅ No new notifications found");
          }
        } else {
          console.warn("⚠️ No data received from notifications API");
        }
      },
      error: (err) => {
        console.warn(
          "Failed to check for new notifications from REST API:",
          err,
        );
      },
    });
  }

  /**
   * Get notifications from REST API that are newer than the last known notification
   */
  private getNewNotificationsFromApi(
    apiNotifications: ApiNotification[],
  ): ApiNotification[] {
    console.log("🔍 Filtering notifications, total:", apiNotifications.length);
    console.log("Last timestamp:", this.lastNotificationTimestamp);
    
    if (!this.lastNotificationTimestamp) {
      // First time check, treat all unread notifications as new
      const unreadNotifications = apiNotifications.filter((n) => !n.read);
      console.log("First check - unread notifications:", unreadNotifications.length);
      return unreadNotifications;
    }

    const lastTimestamp = new Date(this.lastNotificationTimestamp);
    const newNotifications = apiNotifications.filter((notification) => {
      const createdDate = new Date(notification.createdAt);
      const isNewer = createdDate > lastTimestamp;
      const isUnread = !notification.read;
      const shouldShow = isNewer && isUnread;
      
      if (shouldShow) {
        console.log("✅ Notification qualifies:", notification.title, "created:", notification.createdAt);
      }
      
      return shouldShow;
    });
    
    console.log("🆕 Filtered new notifications:", newNotifications.length);
    return newNotifications;
  }

  // Test method - can be called from browser console
  testNotification(): void {
    console.log("🧪 Testing notification display...");
    this.show({
      type: "info",
      title: "Test Notification",
      message: "If you see this, notifications are working!",
      duration: 10000,
    });
    this.maybeShowBrowserNotification(
      "Test Notification",
      "If you see this, browser notifications are working!",
    );
  }

  // TrackBy function for *ngFor
  trackById(index: number, notification: ToastNotification): string {
    return notification.id;
  }
}

// Make test method available globally for debugging
declare global {
  interface Window {
    testNotification?: () => void;
  }
}
